import { ErrorApi, esProblema, problemaGenerico } from './problema';

/**
 * Cliente HTTP del panel. El panel **solo** habla con la API v1 (G62, ADR-0033): cualquier otra ruta se rechaza aquí,
 * antes de salir. Sin excepciones: también la firma de los canales en tiempo real es una operación de la API v1
 * (`createRealtimeChannelAuthorization`, G78; ver src/tiempo-real/echo.ts).
 *
 * Envía el token de la sesión (`Authorization: Bearer`), la clave de idempotencia si se pide y entiende los errores
 * `Problem` del contrato: los lanza como ErrorApi con su código estable.
 */
export interface OpcionesCliente {
    /** Origen de la API, sin `/v1` (vacío: el mismo origen). */
    base: string;
    /** Token de la sesión de Reservaria; null sin sesión (la fase 1 trae el inicio de sesión). */
    obtenerToken: () => string | null;
    fetch?: typeof fetch;
    /**
     * Renueva la sesión con el token de refresco (`refreshSession`) cuando una petición con sesión responde
     * `401 unauthenticated` (el token de acceso dura 15 minutos). Devuelve si hay sesión nueva: entonces la petición se
     * repite una vez, con la misma `Idempotency-Key`. Sin ella (o si no se puede renovar), el 401 llega a quien llamó.
     */
    renovar?: (tokenCaducado: string) => Promise<boolean>;
}

export interface OpcionesPeticion {
    cuerpo?: unknown;
    /** `Idempotency-Key` para las operaciones que la piden (`x-idempotent`). */
    idempotencia?: string;
    senal?: AbortSignal;
    /**
     * `If-Match` con el `ETag` leído (convenciones-api §8): los recursos editables (miembros, roles, negocio, sedes…) lo
     * exigen para cambiarlos; si otra persona los cambió antes, `412 version_conflict`.
     */
    siCoincide?: string;
    /** Sin el token de la sesión ni renovación (`refreshSession`, que manda el token de refresco en el cuerpo). */
    sinSesion?: boolean;
    /** Cabeceras propias de la operación (p. ej. `X-Captcha-Token` de las altas públicas). */
    cabeceras?: Readonly<Record<string, string>>;
    /**
     * La respuesta correcta no es JSON sino texto (p. ej. la plantilla CSV de `getCustomerImportTemplate`): se devuelve tal
     * cual, sin interpretarla. Los errores siguen llegando como `Problem`.
     */
    comoTexto?: boolean;
    /**
     * La respuesta correcta es un archivo (p. ej. el `.pkpass` de `downloadAppleWalletPass`): se pide con este tipo y se
     * devuelve como `Blob`. Los errores siguen llegando como `Problem`.
     */
    comoBlob?: string;
}

export type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Respuesta con su versión (`ETag`), para mandarla después en `If-Match`. */
export interface ConVersion<T> {
    datos: T;
    version: string | null;
}

export interface ClienteApi {
    pedir<T>(metodo: Metodo, ruta: `/v1/${string}`, opciones?: OpcionesPeticion): Promise<T>;
    /** Como `pedir`, y además devuelve el `ETag` de la respuesta. */
    pedirConVersion<T>(metodo: Metodo, ruta: `/v1/${string}`, opciones?: OpcionesPeticion): Promise<ConVersion<T>>;
}

export function crearClienteApi({ base, obtenerToken, fetch: hacerFetch = globalThis.fetch.bind(globalThis), renovar }: OpcionesCliente): ClienteApi {
    async function enviar<T>(metodo: Metodo, ruta: string, opciones: OpcionesPeticion = {}, renovada = false): Promise<ConVersion<T>> {
        const cabeceras: Record<string, string> = {
            Accept: opciones.comoBlob !== undefined ? `${opciones.comoBlob}, application/problem+json` : opciones.comoTexto === true ? 'text/csv, text/plain, application/problem+json' : 'application/json',
        };
        // Un formulario con archivo (`multipart/form-data`, p. ej. `createCustomerImport`) se manda tal cual: el navegador
        // pone el `Content-Type` con su separador. Cualquier otro cuerpo va en JSON.
        const formulario = typeof FormData !== 'undefined' && opciones.cuerpo instanceof FormData;
        const token = opciones.sinSesion === true ? null : obtenerToken();
        if (token !== null && token !== '') {
            cabeceras.Authorization = `Bearer ${token}`;
        }
        if (opciones.cuerpo !== undefined && !formulario) {
            cabeceras['Content-Type'] = 'application/json';
        }
        if (opciones.idempotencia !== undefined) {
            cabeceras['Idempotency-Key'] = opciones.idempotencia;
        }
        Object.assign(cabeceras, opciones.cabeceras ?? {});
        if (opciones.siCoincide !== undefined) {
            cabeceras['If-Match'] = opciones.siCoincide;
        }

        const respuesta = await hacerFetch(`${base}${ruta}`, {
            method: metodo,
            headers: cabeceras,
            body: opciones.cuerpo === undefined ? undefined : formulario ? (opciones.cuerpo as FormData) : JSON.stringify(opciones.cuerpo),
            signal: opciones.senal,
        });

        if (!respuesta.ok) {
            const error = await errorDe(respuesta);
            // Token de acceso caducado: se renueva la sesión una vez y se repite la petición.
            if (error.codigo === 'unauthenticated' && token !== null && token !== '' && !renovada && renovar !== undefined && (await renovar(token).catch(() => false))) {
                return enviar<T>(metodo, ruta, opciones, true);
            }
            throw error;
        }
        const version = respuesta.headers.get('ETag');
        if (respuesta.status === 204) {
            return { datos: undefined as T, version };
        }

        if (opciones.comoBlob !== undefined) {
            return { datos: (await respuesta.blob()) as T, version };
        }
        // Algunas respuestas correctas no traen cuerpo (p. ej. el 202 de requestPasswordReset).
        const texto = await respuesta.text();
        if (opciones.comoTexto === true) {
            return { datos: texto as T, version };
        }
        return { datos: (texto === '' ? undefined : JSON.parse(texto)) as T, version };
    }

    function comprobada<T>(metodo: Metodo, ruta: string, opciones?: OpcionesPeticion): Promise<ConVersion<T>> {
        if (!/^\/v1\/[^?#]/.test(ruta)) {
            return Promise.reject(new Error(`El panel solo usa la API v1: «${ruta}» no es una ruta /v1/.`));
        }
        return enviar<T>(metodo, ruta, opciones);
    }

    return {
        pedir<T>(metodo: Metodo, ruta: `/v1/${string}`, opciones?: OpcionesPeticion): Promise<T> {
            return comprobada<T>(metodo, ruta, opciones).then((r) => r.datos);
        },
        pedirConVersion<T>(metodo: Metodo, ruta: `/v1/${string}`, opciones?: OpcionesPeticion): Promise<ConVersion<T>> {
            return comprobada<T>(metodo, ruta, opciones);
        },
    };
}

async function errorDe(respuesta: Response): Promise<ErrorApi> {
    const reintentar = respuesta.headers.get('Retry-After');
    const segundos = reintentar !== null && /^\d+$/.test(reintentar) ? Number(reintentar) : null;

    let cuerpo: unknown = null;
    if ((respuesta.headers.get('Content-Type') ?? '').includes('json')) {
        cuerpo = await respuesta.json().catch(() => null);
    }

    return new ErrorApi(esProblema(cuerpo) ? cuerpo : problemaGenerico(respuesta.status), segundos);
}
